import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import { Printer, Pencil, ArrowLeft, Download, FileText, ChevronDown } from "lucide-react";

const statusColor = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  in_progress: "bg-yellow-100 text-yellow-900 border-yellow-300",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  closed: "bg-blue-100 text-blue-900 border-blue-300",
};

const ALL_STATUSES = ["draft", "in_progress", "completed", "closed"];

const STATUS_LABEL = {
  draft: "Draft",
  in_progress: "In Progress",
  completed: "Completed",
  closed: "Closed",
};

export default function JobCardView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jc, setJc] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const printRef = useRef(null);

  const changeStatus = async (newStatus) => {
    setStatusBusy(true);
    try {
      const { data } = await api.put(`/job-cards/${id}`, { ...jc, status: newStatus });
      setJc(data);
      toast.success(`Status updated to ${STATUS_LABEL[newStatus]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally {
      setStatusBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/job-cards/${id}`);
      setJc(data);
    })();
  }, [id]);

  if (!jc) {
    return <Layout><div className="p-8 text-slate-500">Loading…</div></Layout>;
  }


  return (
    <Layout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 no-print">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/job-cards")} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div className="relative">
            <button
              onClick={() => setStatusOpen((o) => !o)}
              disabled={statusBusy}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-wider font-medium cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 ${statusColor[jc.status]}`}
            >
              {STATUS_LABEL[jc.status]}
              <ChevronDown className="w-3 h-3" />
            </button>
            {statusOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setStatusOpen(false)} />
                <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                  {ALL_STATUSES.filter((s) => s !== jc.status).map((s) => (
                    <button
                      key={s}
                      onClick={() => { setStatusOpen(false); changeStatus(s); }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                    >
                      <span className={`inline-block w-2 h-2 rounded-full border ${statusColor[s]}`} />
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API_BASE}/job-cards/${id}/pdf`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
          </a>
          <a href={`${API_BASE}/job-cards/${id}/approval-sheet`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline"><FileText className="w-4 h-4 mr-1" /> Approval Sheet</Button>
          </a>
          <Link to={`/job-cards/${id}/edit`}><Button variant="outline" data-testid="edit-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button></Link>
          <Button onClick={() => window.open(`${API_BASE}/job-cards/${id}/pdf`, "_blank")} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div ref={printRef} className="print-page">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="CTI" className="w-14 h-14 object-contain" />
            <div>
              <div className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>CTI Printing Line</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Production Job Card</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Job Card No.</div>
            <div className="text-xl font-mono text-slate-900 mt-1" data-testid="print-jc-no">{jc.job_card_no}</div>
            <div className="text-[10px] text-slate-500 mt-1">
              Created: {new Date(jc.created_at).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-500">
              By: <span className="font-mono">{jc.created_by_name || "—"}</span>
            </div>
            {jc.updated_at && jc.updated_at !== jc.created_at && (
              <div className="text-[10px] text-slate-500">
                Updated: {new Date(jc.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Customer & PO */}
        <SectionTitle n="01" title="Customer & Purchase Order" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-6 text-sm">
          <Row label="Customer" value={jc.customer_name} />
          <Row label="Customer PO" value={jc.customer_po} mono />
          <Row label="Contact Person" value={jc.customer_contact || "—"} />
          <Row label="Phone" value={jc.customer_phone || "—"} />
          <Row label="Email" value={jc.customer_email || "—"} />
          <Row label="GST No." value={jc.customer_gst || "—"} mono />
          <Row label="Address" value={jc.customer_address || "—"} full />
          <Row label="Order Quality" value={jc.artwork_version || "—"} mono />
          <Row label="Due Date" value={jc.due_date || "—"} />
          <Row label="Product Description" value={jc.product_description || "—"} full />
          <Row label="Created By" value={jc.created_by_name || "—"} />
        </div>

        {/* Artwork Approval Sheet — embedded PDF */}
        <SectionTitle n="02" title="Artwork Approval Sheet" />
        <div className="mb-4 no-print">
          <div className="border-2 border-[#1d3d8f] rounded-lg overflow-hidden bg-white relative" style={{ minHeight: "620px" }}>
            <ApprovalSheetFrame id={id} apiBase={API_BASE} updatedAt={jc.updated_at || jc.created_at || ""} />
          </div>
          <div className="mt-2 flex justify-end">
            <a href={`${API_BASE}/job-cards/${id}/approval-sheet`} target="_blank" rel="noopener noreferrer">
              <button className="flex items-center gap-2 px-4 py-2 rounded border border-[#1d3d8f] text-[#1d3d8f] text-sm font-medium hover:bg-[#1d3d8f] hover:text-white transition-colors">
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </a>
          </div>
        </div>

        {/* Materials */}
        <SectionTitle n="03" title="Material Issue & Consumption" />
        <PrintTable
          empty="No materials recorded"
          rows={jc.materials || []}
          columns={[
            { h: "Material", get: (m) => m.material_name },
            { h: "Unit", get: (m) => m.unit },
            { h: "Issued", get: (m) => m.issued_qty, right: true, mono: true },
            { h: "Consumed", get: (m) => m.consumed_qty, right: true, mono: true },
            { h: "Remarks", get: (m) => m.remarks },
          ]}
        />

        {/* Production log */}
        <SectionTitle n="04" title="Machine-wise Production Log" />
        <PrintTable
          empty="No production log recorded"
          rows={jc.production_logs || []}
          columns={[
            { h: "Machine", get: (p) => p.machine },
            { h: "Operator", get: (p) => p.operator },
            { h: "Start", get: (p) => (p.start_time || "").replace("T", " ") },
            { h: "End", get: (p) => (p.end_time || "").replace("T", " ") },
            { h: "Output", get: (p) => p.output_qty, right: true, mono: true },
            { h: "Remarks", get: (p) => p.remarks },
          ]}
        />

        {/* Reconciliation */}
        <SectionTitle n="05" title="Reconciliation" />
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Printing</div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Metric label="Good" value={jc.good_qty} tone="ok" />
          <Metric label="Rejected" value={jc.rejection_qty} tone="warn" />
          <Metric label="Wastage" value={jc.wastage_qty} tone="warn" />
        </div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Production</div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Metric label="Good" value={jc.production_good_qty} tone="ok" />
          <Metric label="Rejected" value={jc.production_rejection_qty} tone="warn" />
          <Metric label="Wastage" value={jc.production_wastage_qty} tone="warn" />
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Metric label="Operator" value={jc.operator_name || "—"} />
        </div>

        {/* Checklist */}
        <SectionTitle n="06" title="Checklist" />
        <div className="grid grid-cols-2 gap-2 mb-6 text-sm">
          {((jc.checklist && jc.checklist.length ? jc.checklist : jc.packing_checklist) || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-4 h-4 border border-slate-500 flex items-center justify-center ${c.checked ? "bg-slate-900" : "bg-white"}`}>
                {c.checked && <span className="text-white text-[10px]">✓</span>}
              </div>
              <span>{c.label}</span>
            </div>
          ))}
        </div>

        {(() => {
          // Build attachments source: only first 2 images per item (front + back), no reference images
          const perItem = (jc.items || [])
            .map((it, idx) => ({ idx, name: it.item_name || `Item ${idx + 1}`, files: (it.artwork_files || []).slice(0, 2) }))
            .filter((g) => g.files.length > 0);
          const legacy = (jc.artwork_files || []);
          const hasAny = perItem.length > 0 || legacy.length > 0;
          if (!hasAny) return null;
          const renderFile = (f) => {
            const url = f.storage_path ? `${API_BASE.replace("/api", "")}/uploads/${f.storage_path}` : `${API_BASE}/files/${f.id}`;
            const isImage = (f.content_type || "").startsWith("image/");
            const isPdf = f.content_type === "application/pdf";
            return (
              <div key={f.id} className="border border-slate-200 rounded overflow-hidden bg-slate-50">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white text-sm">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="truncate flex-1 font-medium">{f.original_filename}</span>
                  <span className="text-xs text-slate-500 font-mono">{(f.size / 1024).toFixed(1)} KB</span>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-700 hover:text-slate-900 font-medium">Open</a>
                </div>
                {isImage ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={url} alt={f.original_filename} className="max-h-[600px] w-full object-contain bg-white" />
                  </a>
                ) : isPdf ? (
                  <iframe src={url} title={f.original_filename} className="w-full bg-white" style={{ height: "600px", border: "none" }} />
                ) : (
                  <div className="p-4 bg-white">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 font-medium">
                      <FileText className="w-4 h-4" /> Download {f.original_filename}
                    </a>
                  </div>
                )}
              </div>
            );
          };
          return (
            <>
              <SectionTitle n="A" title="Attached Artwork" />
              <div className="space-y-6 mb-6 no-print">
                {perItem.map((g) => (
                  <div key={g.idx}>
                    <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold mb-2">
                      #{g.idx + 1} · {g.name}
                    </div>
                    <div className="grid grid-cols-1 gap-4">{g.files.map(renderFile)}</div>
                  </div>
                ))}
                {legacy.length > 0 && (
                  <div>
                    {perItem.length > 0 && <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold mb-2">Other</div>}
                    <div className="grid grid-cols-1 gap-4">{legacy.map(renderFile)}</div>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {jc.notes && (
          <>
            <SectionTitle n="07" title="Notes" />
            <div className="text-sm text-slate-700 mb-6 whitespace-pre-wrap border border-slate-200 rounded p-3">{jc.notes}</div>
          </>
        )}

        {/* Footer: Barcode + Signatures + QR */}
        <div className="mt-6 pt-6 border-t-2 border-slate-900 flex gap-6 items-start">
          {/* Barcode — fixed width so it never overflows */}
          <div className="shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Barcode</div>
            <div className="bg-white max-w-[200px] overflow-hidden">
              <Barcode value={jc.job_card_no} format="CODE128" height={50} width={1.3} fontSize={11} displayValue={true} />
            </div>
          </div>
          {/* Supervisor + QA signatures — flex-grow to fill space */}
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Signatures</div>
            <div className="grid grid-cols-2 gap-6 mt-3 text-xs">
              {[["supervisor", "Supervisor"], ["qa", "QA"]].map(([role, label]) => {
                const entry = (jc.footer_signoffs || {})[role];
                return (
                  <FooterSignatureBox
                    key={role}
                    role={role}
                    label={label}
                    entry={entry}
                    jcId={jc.id}
                    onSigned={(e) => setJc({ ...jc, footer_signoffs: { ...(jc.footer_signoffs || {}), [role]: e } })}
                  />
                );
              })}
            </div>
          </div>
          {/* QR Code — links to this job card page */}
          <div className="shrink-0 text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">QR Code</div>
            <div className="inline-block bg-white p-1 border border-slate-200">
              <QRCodeSVG value={`${window.location.origin}/job-cards/${jc.id}`} size={82} />
            </div>
            <div className="text-[10px] text-slate-500 mt-1 font-mono">{jc.job_card_no}</div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function SectionTitle({ n, title }) {
  return (
    <div className="flex items-center gap-3 mb-3 mt-4">
      <div className="text-[10px] font-mono bg-slate-900 text-white px-1.5 py-0.5">{n}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-800">{title}</div>
      <div className="flex-1 h-px bg-slate-200"></div>
    </div>
  );
}

function Row({ label, value, mono, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm text-slate-900 break-words ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const color = tone === "warn" ? "text-yellow-700" : tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="border border-slate-300 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-mono ${color}`}>{value ?? 0}</div>
    </div>
  );
}

function ApprovalSection({ jc, onUpdate }) {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [comments, setComments] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const a = jc.artwork_approval;

  const submit = async (decision) => {
    if (!name.trim()) { toast.error("Approver name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/job-cards/${jc.id}/approval`, {
        approver_name: name, approver_email: email, decision, comments,
      });
      onUpdate(data);
      toast.success(`Artwork ${decision}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (a) {
    const isApproved = a.decision === "approved";
    const border = isApproved ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50";
    const chip = isApproved ? "bg-emerald-600 text-white" : "bg-red-600 text-white";
    return (
      <div className={`border ${border} rounded p-4 mb-6`}>
        <div className="flex items-center gap-3 mb-2">
          <div className="text-[10px] font-mono bg-slate-900 text-white px-1.5 py-0.5">09</div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-800">Customer Artwork Sign-off</div>
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${chip}`} data-testid="approval-status">{a.decision}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <div><span className="text-slate-500 text-xs uppercase tracking-widest">Approver:</span> {a.approver_name}</div>
          <div><span className="text-slate-500 text-xs uppercase tracking-widest">Email:</span> <span className="font-mono text-xs">{a.approver_email || "—"}</span></div>
          <div><span className="text-slate-500 text-xs uppercase tracking-widest">Signed:</span> {new Date(a.signed_at).toLocaleString()}</div>
          <div><span className="text-slate-500 text-xs uppercase tracking-widest">Captured By:</span> {a.signed_by_user_name}</div>
          {a.comments && <div className="col-span-2 mt-2"><span className="text-slate-500 text-xs uppercase tracking-widest">Comments:</span> {a.comments}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded p-4 mb-6 no-print">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-[10px] font-mono bg-slate-900 text-white px-1.5 py-0.5">09</div>
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-800">Customer Artwork Sign-off</div>
      </div>
      <p className="text-xs text-slate-500 mb-3">Capture customer approval or rejection of the artwork. Once recorded, decision is locked on the job card and PDF.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input placeholder="Approver name" value={name} onChange={(e) => setName(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="approval-name" />
        <input placeholder="Approver email" value={email} onChange={(e) => setEmail(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="approval-email" />
        <input placeholder="Comments (optional)" value={comments} onChange={(e) => setComments(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="approval-comments" />
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button" onClick={() => submit("approved")} disabled={busy} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50" data-testid="approve-btn">Approve</button>
        <button type="button" onClick={() => submit("rejected")} disabled={busy} className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50" data-testid="reject-btn">Reject</button>
      </div>
    </div>
  );
}

// Small SVG icons matching the CTI reference sheet style
function RowIcon({ name }) {
  const s = 18;
  const c = "#1d3d8f";
  const icons = {
    job:      <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="5" width="14" height="10" rx="1.5" stroke={c} strokeWidth="1.4"/><path d="M6 5V4a3 3 0 016 0v1" stroke={c} strokeWidth="1.4"/><line x1="2" y1="9" x2="16" y2="9" stroke={c} strokeWidth="1.2"/></svg>,
    size:     <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="2" width="14" height="14" rx="1.5" stroke={c} strokeWidth="1.4"/><line x1="5" y1="2" x2="5" y2="16" stroke={c} strokeWidth="1"/><line x1="2" y1="13" x2="16" y2="13" stroke={c} strokeWidth="1"/><line x1="5" y1="5" x2="7" y2="5" stroke={c} strokeWidth="1.2"/><line x1="5" y1="8" x2="7" y2="8" stroke={c} strokeWidth="1.2"/><line x1="8" y1="13" x2="8" y2="15" stroke={c} strokeWidth="1.2"/><line x1="11" y1="13" x2="11" y2="15" stroke={c} strokeWidth="1.2"/></svg>,
    date:     <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="13" rx="1.5" stroke={c} strokeWidth="1.4"/><line x1="2" y1="7" x2="16" y2="7" stroke={c} strokeWidth="1.3"/><line x1="6" y1="1" x2="6" y2="5" stroke={c} strokeWidth="1.4"/><line x1="12" y1="1" x2="12" y2="5" stroke={c} strokeWidth="1.4"/><rect x="5" y="9" width="2.5" height="2.5" rx="0.5" fill={c}/><rect x="9" y="9" width="2.5" height="2.5" rx="0.5" fill={c}/></svg>,
    version:  <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke={c} strokeWidth="1.4"/><path d="M6 9l2.5 2.5L13 7" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    emboss:   <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="14" height="10" rx="2" stroke={c} strokeWidth="1.4"/><rect x="4" y="6" width="10" height="6" rx="1" stroke={c} strokeWidth="1"/></svg>,
    cardtype: <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="1" y="4" width="16" height="10" rx="2" stroke={c} strokeWidth="1.4"/><line x1="1" y1="8" x2="17" y2="8" stroke={c} strokeWidth="1.4"/><rect x="3" y="10" width="4" height="1.5" rx="0.5" fill={c}/></svg>,
    notes:    <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="3" y="2" width="12" height="14" rx="1.5" stroke={c} strokeWidth="1.4"/><line x1="6" y1="6" x2="12" y2="6" stroke={c} strokeWidth="1.2"/><line x1="6" y1="9" x2="12" y2="9" stroke={c} strokeWidth="1.2"/><line x1="6" y1="12" x2="10" y2="12" stroke={c} strokeWidth="1.2"/></svg>,
    printer:  <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="3" y="6" width="12" height="8" rx="1.5" stroke={c} strokeWidth="1.4"/><path d="M5 6V3h8v3" stroke={c} strokeWidth="1.3"/><rect x="5" y="10" width="8" height="3" rx="0.5" fill={c} opacity="0.2" stroke={c} strokeWidth="1"/><circle cx="13" cy="8.5" r="1" fill={c}/></svg>,
    finish:   <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><path d="M4 14l3-5 3 3 3-5 2 7" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 4c4 0 6 4 10 2" stroke={c} strokeWidth="1.3" strokeLinecap="round"/></svg>,
    tagno:    <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="10" height="10" rx="1.5" stroke={c} strokeWidth="1.4"/><line x1="4" y1="8" x2="10" y2="8" stroke={c} strokeWidth="1.1"/><line x1="4" y1="11" x2="8" y2="11" stroke={c} strokeWidth="1.1"/><path d="M13 5l3 3-3 3" stroke={c} strokeWidth="1.3" strokeLinecap="round"/></svg>,
    qr:       <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="2" width="6" height="6" rx="1" stroke={c} strokeWidth="1.3"/><rect x="3.5" y="3.5" width="3" height="3" fill={c}/><rect x="10" y="2" width="6" height="6" rx="1" stroke={c} strokeWidth="1.3"/><rect x="11.5" y="3.5" width="3" height="3" fill={c}/><rect x="2" y="10" width="6" height="6" rx="1" stroke={c} strokeWidth="1.3"/><rect x="3.5" y="11.5" width="3" height="3" fill={c}/><rect x="10" y="10" width="2.5" height="2.5" fill={c}/><rect x="13.5" y="10" width="2.5" height="2.5" fill={c}/><rect x="10" y="13.5" width="2.5" height="2.5" fill={c}/></svg>,
    chip:     <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="5" y="5" width="8" height="8" rx="1" stroke={c} strokeWidth="1.4"/><rect x="7" y="7" width="4" height="4" rx="0.5" stroke={c} strokeWidth="1"/><line x1="7" y1="2" x2="7" y2="5" stroke={c} strokeWidth="1.2"/><line x1="11" y1="2" x2="11" y2="5" stroke={c} strokeWidth="1.2"/><line x1="7" y1="13" x2="7" y2="16" stroke={c} strokeWidth="1.2"/><line x1="11" y1="13" x2="11" y2="16" stroke={c} strokeWidth="1.2"/><line x1="2" y1="7" x2="5" y2="7" stroke={c} strokeWidth="1.2"/><line x1="2" y1="11" x2="5" y2="11" stroke={c} strokeWidth="1.2"/><line x1="13" y1="7" x2="16" y2="7" stroke={c} strokeWidth="1.2"/><line x1="13" y1="11" x2="16" y2="11" stroke={c} strokeWidth="1.2"/></svg>,
    vardata:  <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><ellipse cx="9" cy="13" rx="7" ry="3" stroke={c} strokeWidth="1.3"/><ellipse cx="9" cy="9" rx="7" ry="3" stroke={c} strokeWidth="1.3"/><path d="M2 9v4M16 9v4" stroke={c} strokeWidth="1.3"/><ellipse cx="9" cy="5" rx="7" ry="3" stroke={c} strokeWidth="1.3"/><path d="M2 5v4M16 5v4" stroke={c} strokeWidth="1.3"/></svg>,
    thick:    <svg width={s} height={s} viewBox="0 0 18 18" fill="none"><rect x="2" y="5" width="14" height="4" rx="1" stroke={c} strokeWidth="1.4"/><rect x="2" y="11" width="14" height="2" rx="0.5" fill={c} opacity="0.3" stroke={c} strokeWidth="1"/><line x1="9" y1="3" x2="9" y2="5" stroke={c} strokeWidth="1.2"/><line x1="9" y1="13" x2="9" y2="15" stroke={c} strokeWidth="1.2"/><line x1="6" y1="3" x2="12" y2="3" stroke={c} strokeWidth="1.2"/><line x1="6" y1="15" x2="12" y2="15" stroke={c} strokeWidth="1.2"/></svg>,
  };
  return <span className="inline-flex items-center justify-center w-5 h-5">{icons[name] || null}</span>;
}

function ApprovalCard({ idx, it, jc, apiBase }) {
  const jobName = [jc.customer_name, it.item_name].filter(Boolean).join(" — ") || "—";
  const jobDate = jc.due_date || (jc.created_at ? new Date(jc.created_at).toISOString().slice(0, 10) : "—");
  const docNo = jc.job_card_no || "—";

  const leftRows = [
    { icon: "job",     label: "JOB NAME",        value: jobName },
    { icon: "size",    label: "TAG / CARD SIZE",  value: it.card_size },
    { icon: "date",    label: "DATE",             value: jobDate },
    { icon: "version", label: "FILE LOCATION",     value: jc.artwork_version },
    { icon: "emboss",  label: "EMBOSSING",        value: it.embossing },
    { icon: "cardtype",label: "TYPE OF CARD",     value: it.type_of_card || it.item_type },
    { icon: "notes",   label: "NOTES (IF ANY)",   value: jc.notes },
  ];
  const rightRows = [
    { icon: "printer", label: "PRINTING",             value: it.print_type },
    { icon: "finish",  label: "FINISH",               value: it.finish },
    { icon: "tagno",   label: "TAG / CARD NO.",        value: it.tag_card_no },
    { icon: "qr",      label: "QR LINK",              value: it.qr_link },
    { icon: "chip",    label: "CHIP NO. / ENCODING",  value: it.chip_encoding },
    { icon: "vardata", label: "VARIABLE DATA",        value: it.variable_data },
    { icon: "thick",   label: "THICKNESS",            value: it.thickness },
  ];

  const artworkFiles = it.artwork_files || [];
  const frontImg = artworkFiles.find((f) => /front/i.test(f.original_filename || "")) || artworkFiles[0];
  const backImg  = artworkFiles.find((f) => /back/i.test(f.original_filename || "")) || artworkFiles[1];
  const imgUrl = (f) => f?.storage_path
    ? `${apiBase.replace("/api", "")}/uploads/${f.storage_path}`
    : f?.id ? `${apiBase}/files/${f.id}` : null;

  return (
    <div className="border-2 border-[#1d3d8f] rounded-lg overflow-hidden bg-white shadow-sm">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b-2 border-[#1d3d8f]">
        <div className="flex items-center gap-3">
          <img src="/brand/logo.png" alt="CTI" className="h-12 w-12 object-contain" />
          <div>
            <div className="text-base font-bold text-[#1d3d8f] leading-tight">CTI Printing Line</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">India Private Limited</div>
          </div>
        </div>
        <div className="text-center flex-1 px-4">
          <div className="text-xl font-black uppercase tracking-widest text-[#1d3d8f] whitespace-nowrap">ARTWORK APPROVAL SHEET</div>
        </div>
        <div className="border-2 border-[#1d3d8f] rounded-lg overflow-hidden text-sm min-w-[160px]">
          <div className="px-3 py-1.5 border-b border-[#1d3d8f]">
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Document No.</div>
            <div className="font-mono font-bold text-[#1d3d8f] text-sm">{docNo}</div>
          </div>
          <div className="px-3 py-1.5">
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Date</div>
            <div className="font-bold text-slate-800 text-sm">{jobDate}</div>
          </div>
        </div>
      </div>

      {/* ── FRONT / BACK IMAGES ── */}
      <div className="grid grid-cols-2 bg-white py-4 px-4 gap-4 border-b border-slate-200">
        {[["FRONT SIDE", frontImg], ["BACK SIDE", backImg]].map(([label, f], i) => {
          const url = imgUrl(f);
          const isImg = f && (f.content_type || "").startsWith("image/");
          return (
            <div key={i} className="flex flex-col items-center gap-3">
              <div className="bg-[#1d3d8f] text-white text-[11px] font-bold tracking-widest px-5 py-1.5 rounded-full">{label}</div>
              <div className="border-2 border-[#c9d1dd] rounded-xl w-full flex items-center justify-center bg-slate-50" style={{ minHeight: 180 }}>
                {url && isImg ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 w-full">
                    <img src={url} alt={label} className="max-h-44 max-w-full object-contain" />
                  </a>
                ) : (
                  <div className="text-[11px] text-slate-400 italic py-10">No image</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── DETAIL TABLES ── */}
      <div className="grid grid-cols-2 border-b border-slate-200">
        {/* JOB DETAILS */}
        <div className="flex border-r border-slate-200">
          {/* rotated side label */}
          <div className="flex items-center justify-center bg-[#1d3d8f] text-white" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", padding: "12px 7px", whiteSpace: "nowrap" }}>
            JOB DETAILS
          </div>
          <table className="flex-1 text-xs border-collapse">
            <tbody>
              {leftRows.map(({ icon, label, value }) => (
                <tr key={label} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1.5 w-7 text-center"><RowIcon name={icon} /></td>
                  <td className="px-2 py-1.5 text-[10px] font-bold text-[#1d3d8f] uppercase tracking-wide whitespace-nowrap w-36">{label}</td>
                  <td className="px-2 py-1.5 text-slate-800">{value || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PRINTING & TECHNICAL DETAILS */}
        <div className="flex">
          <div className="flex items-center justify-center bg-[#1d3d8f] text-white" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", padding: "12px 7px", whiteSpace: "nowrap" }}>
            PRINTING AND TECHNICAL DETAILS
          </div>
          <table className="flex-1 text-xs border-collapse">
            <tbody>
              {rightRows.map(({ icon, label, value }) => (
                <tr key={label} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1.5 w-7 text-center"><RowIcon name={icon} /></td>
                  <td className="px-2 py-1.5 text-[10px] font-bold text-[#1d3d8f] uppercase tracking-wide whitespace-nowrap w-40">{label}</td>
                  <td className="px-2 py-1.5 text-slate-800">{value || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── FOOTER: 3 BOXES ── */}
      <div className="grid grid-cols-3 gap-0 divide-x divide-slate-200">

        {/* CLIENT APPROVAL */}
        <div className="overflow-hidden">
          <div className="bg-[#3fae49] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5" style={{ clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 100%, 0 100%)" }}>
            CLIENT APPROVAL
          </div>
          <div className="px-3 py-3 text-xs text-slate-700">
            We have reviewed the above design &amp; confirm approval for production
          </div>
          <div className="px-3 pb-3 mt-2">
            <div className="border-b border-slate-400 h-6 mb-1"></div>
            <div className="text-[9px] text-slate-500 uppercase tracking-widest">Signature &amp; Date</div>
          </div>
        </div>

        {/* INTERNAL USE ONLY */}
        <div className="overflow-hidden">
          <div className="bg-[#4843ab] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5" style={{ clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 100%, 0 100%)" }}>
            INTERNAL USE ONLY
          </div>
          <table className="w-full text-[9px] border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                {["DEPARTMENT", "NAME", "SIGNATURE", "DATE"].map((h) => (
                  <th key={h} className="px-2 py-1 text-[8px] font-bold text-[#4843ab] uppercase tracking-wide text-left border-r border-slate-100 last:border-0">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {["DESIGN", "PRE-PRESS", "PRODUCTION", "QUALITY CONTROL"].map((dept) => (
                <tr key={dept} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1.5 font-bold text-[#4843ab] text-[8px] border-r border-slate-100">{dept}</td>
                  <td className="px-2 py-1.5 border-r border-slate-100"></td>
                  <td className="px-2 py-1.5 border-r border-slate-100"></td>
                  <td className="px-2 py-1.5"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* IMPORTANT NOTE */}
        <div className="overflow-hidden">
          <div className="bg-[#e6402e] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 flex items-center gap-2" style={{ clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 100%, 0 100%)" }}>
            <span>⚠️</span> IMPORTANT NOTE
          </div>
          <div className="px-3 py-3 text-[10px] text-slate-700 leading-relaxed">
            This design's colour scheme &amp; alignment may vary during production. And please carefully check the complete design, text matter company will not be responsible for any change after printing
          </div>
        </div>
      </div>
    </div>
  );
}

const DEPARTMENTS = ["DESIGN", "PRE-PRESS", "PRODUCTION", "QUALITY CONTROL"];

function InternalSignoffSection({ jc, onUpdate }) {
  const [inputs, setInputs] = React.useState({});
  const [busy, setBusy] = React.useState({});
  const signoffs = jc.internal_signoffs || {};

  const sign = async (dept) => {
    const name = (inputs[dept] || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy((b) => ({ ...b, [dept]: true }));
    try {
      const { data } = await api.post(`/job-cards/${jc.id}/internal-signoff`, {
        department: dept, name,
      });
      onUpdate({ ...signoffs, [dept]: data });
      setInputs((i) => ({ ...i, [dept]: "" }));
      toast.success(`${dept} signed off`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [dept]: false }));
    }
  };

  return (
    <div className="mb-6 no-print">
      <SectionTitle n="08" title="Internal Sign-offs" />
      <div className="border-2 border-[#4843ab] rounded-lg overflow-hidden">
        <div className="bg-[#4843ab] text-white text-[11px] font-bold uppercase tracking-widest px-4 py-2">
          Internal Use Only
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {["Department", "Name", "Signature", "Date", "Action"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] font-bold text-[#4843ab] uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DEPARTMENTS.map((dept) => {
              const entry = signoffs[dept];
              return (
                <tr key={dept} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2 font-bold text-[#4843ab] text-xs uppercase tracking-wide">{dept}</td>
                  {entry ? (
                    <>
                      <td className="px-3 py-2 text-slate-800">{entry.name}</td>
                      <td className="px-3 py-2 italic text-slate-600">{entry.signature || entry.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{(entry.signed_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">✓ Signed</span>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-1.5" colSpan={2}>
                        <input
                          placeholder="Enter name to sign off"
                          value={inputs[dept] || ""}
                          onChange={(e) => setInputs((i) => ({ ...i, [dept]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && sign(dept)}
                          className="border border-slate-300 rounded px-2 py-1 text-sm w-full"
                        />
                      </td>
                      <td className="px-3 py-1.5 text-slate-400 text-xs">—</td>
                      <td className="px-3 py-1.5">
                        <button
                          onClick={() => sign(dept)}
                          disabled={busy[dept]}
                          className="px-3 py-1 rounded bg-[#4843ab] hover:bg-[#3a3590] text-white text-xs font-medium disabled:opacity-50"
                        >
                          Sign Off
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FooterSignatureBox({ role, label, entry, jcId, onSigned }) {
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const sign = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/job-cards/${jcId}/footer-signoff`, { role, name });
      onSigned(data);
      setName("");
      toast.success(`${label} signed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (entry) {
    return (
      <div>
        <div className="border-b border-slate-800 h-8 flex items-end justify-center pb-1">
          <span className="text-xs font-medium text-slate-800 italic">{entry.name}</span>
        </div>
        <div className="mt-1 text-slate-500 text-[10px]">{label}</div>
        <div className="text-[9px] text-slate-400 font-mono">{(entry.signed_at || "").slice(0, 10)}</div>
      </div>
    );
  }

  return (
    <div className="no-print">
      <div className="border-b border-slate-800 h-8 flex items-end gap-1 pb-1">
        <input
          placeholder={`${label} name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sign()}
          className="border-0 border-b border-slate-300 text-xs px-1 py-0 w-full focus:outline-none focus:border-slate-600"
        />
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-slate-500 text-[10px]">{label}</span>
        <button onClick={sign} disabled={busy} className="text-[9px] px-1.5 py-0.5 bg-slate-800 text-white rounded disabled:opacity-50">Sign</button>
      </div>
    </div>
  );
}

function ApprovalSheetFrame({ id, apiBase, updatedAt }) {
  const [loaded, setLoaded] = React.useState(false);
  const v = encodeURIComponent(updatedAt || "");
  React.useEffect(() => { setLoaded(false); }, [updatedAt]);
  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white z-10">
          <div className="w-10 h-10 border-4 border-[#1d3d8f] border-t-transparent rounded-full animate-spin" />
          <div className="text-sm text-slate-500">Generating approval sheet…</div>
          <a href={`${apiBase}/job-cards/${id}/approval-sheet`} target="_blank" rel="noopener noreferrer"
             className="text-xs text-[#1d3d8f] underline">Download PDF instead</a>
        </div>
      )}
      <img
        src={`${apiBase}/job-cards/${id}/approval-sheet/preview?v=${v}`}
        alt="Approval Sheet"
        className="w-full block"
        style={{ display: loaded ? "block" : "none" }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </>
  );
}

function PrintTable({ rows, columns, empty }) {  if (!rows || rows.length === 0) {
    return <div className="text-sm text-slate-500 border border-slate-200 rounded p-3 mb-6">{empty}</div>;
  }
  return (
    <div className="mb-6 border border-slate-300">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-100 border-b border-slate-300 text-[10px] uppercase tracking-widest text-slate-700">
            {columns.map((c) => (
              <th key={c.h} className={`px-3 py-2 text-left ${c.right ? "text-right" : ""}`}>{c.h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
              {columns.map((c) => (
                <td key={c.h} className={`px-3 py-2 ${c.right ? "text-right" : ""} ${c.mono ? "font-mono" : ""}`}>{c.get(r) ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
